import { Call, CallEvent, CallType } from '@letsdial/api/src/lib/types/Call'
import { DateTime } from 'luxon'
import { defineStore } from 'pinia'
import { CallStatusTypes } from '@letsdial/phone/src/lib/types'
import useLetsDialAPI from '../services/letsDialAPI/useLetsDialAPI'
import { CampaignDisposition } from '@letsdial/api/src/lib/types/CampaignDisposition'
import { IncomingCallEvent, IncomingTransferEvent } from '@letsdial/socket'
import useLetsDialSocket from '../services/letsDialSocket/useLetsDialSocket'
import { useNotificationStore } from './notificationStore'
import { useRecordStore } from './recordStore'
import useGetStream from '../services/letsDialSocket/useGetStream'
import { useAuthStore } from './authStore'
import { OrganizationDisposition } from '@letsdial/api/src/lib/types/OrganizationDisposition'

export interface IncomingTransfer extends IncomingTransferEvent {
  promptedAt?: DateTime
}

export interface IncomingCall extends IncomingCallEvent {
  promptedAt?: DateTime
}

let socketClose: any

export const useCallStore = defineStore({
  // unique id of the store across your application
  id: 'call',
  state: () =>
    ({
      call: undefined,
      muted: false,
      status: undefined,
      error: undefined,
      statusChangedAt: DateTime.now(),
      incomingTransfer: undefined,
      incomingCall: undefined,
      conferenced: false,
      conferenceRoomId: undefined,
      showDisposition: false,
      campaignDispositions: [],
      organizationDispositions: [],
    }) as {
      call?: Call
      muted: false
      error?: string
      status?: string
      statusChangedAt?: DateTime
      conferenced: boolean
      conferenceRoomId?: string | number
      incomingTransfer?: IncomingTransfer
      incomingCall?: IncomingCallEvent
      showDisposition?: boolean
      campaignDispositions?: Array<CampaignDisposition>
      organizationDispositions?: Array<OrganizationDisposition>
    },
  getters: {
    hasDispositions(state) {
      return (
        (state.campaignDispositions && state.campaignDispositions.length > 0) ||
        (state.organizationDispositions &&
          state.organizationDispositions.length > 0)
      )
    },
    onCall(state) {
      switch (state.status) {
        // case CallStatusTypes.
        case CallStatusTypes.INITIATING:
        case CallStatusTypes.RINGING:
        case CallStatusTypes.ANSWERED:
          return true
      }
      return false
    },
  },
  actions: {
    async mount() {
      this.loadSocket()
    },
    unmount() {
      if (socketClose) {
        socketClose()
      }
    },
    loadSocket() {
      useLetsDialSocket()
      const { leave } = useGetStream<CallEvent>(
        'calls:stream',
        { filters: { userId: 'me' } },
        (event: CallEvent) => {
          setTimeout(() => {
            if (event.id === this.call?.id) {
              for (const [key, value] of Object.entries(event.data)) {
                this.setAttribute(key, value)
              }
              if (event.data.killed) {
                this.killCall(event.id, event.data.killReason)
              }
            }
          }, 100)
        },
      )
      socketClose = leave
    },
    async killCall(callId: number, killReason?: string): Promise<void> {
      if (callId === this.call?.id) {
        const recordStore = useRecordStore()
        const reasonMessages: Record<string, string> = {
          'VOICEMAIL_DETECTED': 'Call was detected to be a voicemail',
          'EARLY_ANSWER_DETECTION': 'Call was terminated due to early answer detection',
          'RING_TIME_NO_ANSWER': 'Call was terminated due to ring time exceeded',
        }
        const message = reasonMessages[killReason ?? ''] ?? 'Call was terminated by the system'
        useNotificationStore().setNotification('Call was killed', message, 'Warning', undefined, 5000)
        this.$reset()
        recordStore.$reset()
        const authStore = useAuthStore()
        authStore.setAvailabilityStatus('IDLE')
      }
    },
    async getCall(id: number) {
      const api = useLetsDialAPI()
      this.setCall((await api.calls.get(id)).data ?? undefined)
    },
    async setShowDisposition() {
      this.showDisposition = true
    },
    async setStatus(status: string) {
      this.status = status
      this.statusChangedAt = DateTime.now()
      const api = useLetsDialAPI()
      const currentCallId = this.call?.id
      if (currentCallId) {
        try {
          await api.calls.patch(currentCallId, {
            id: currentCallId,
            type: 'calls',
            attributes: {
              callStatus: status,
            },
          })
          // Double check call hasn't been cleared mid-request
          if (this.call?.id === currentCallId) {
            this.status = status
            this.statusChangedAt = DateTime.now()
          }
        } catch (e) {
          console.error('Failed to set call status', e)
        }
      } else {
        this.status = status
        this.statusChangedAt = DateTime.now()
      }
    },
    setConferenced(conferenced = false) {
      this.conferenced = conferenced
    },
    setAttribute(attribute: string, value: string) {
      if (this.call && this.call.attributes) {
        ;(this.call.attributes as any)[attribute] = value
      }
      if (attribute === 'status') {
        this.status = value
        this.statusChangedAt = DateTime.now()
      }
    },
    setRoomId(roomId?: number | string) {
      this.conferenceRoomId = roomId
    },
    setIncomingTransfer(transfer?: IncomingTransfer) {
      if (transfer) {
        transfer.promptedAt = DateTime.now()
      }
      this.incomingTransfer = transfer
    },
    async acceptTransfer() {
      if (this.incomingTransfer?.callID) {
        await this.getCall(this.incomingTransfer?.callID)
      }
      this.setIncomingTransfer(undefined)
    },
    declineTransfer() {
      // Intentional empty method
    },
    setIncomingCall(incomingCall?: IncomingCall) {
      if (incomingCall) {
        incomingCall.promptedAt = DateTime.now()
      }
      this.incomingCall = incomingCall
    },
    async acceptIncomingCall() {
      if (this.incomingCall?.callID) {
        await this.getCall(this.incomingCall?.callID)
      }
      if (this.incomingCall?.recordID) {
        const recordStore = useRecordStore()
        await recordStore.getRecord(this.incomingCall.recordID)
      }
      this.setIncomingCall(undefined)
    },
    declineIncomingCall() {
      this.setIncomingCall(undefined)
    },
    setCall(call?: Call) {
      console.log('setCall', call)
      this.call = { ...call } as Call
      console.log('setCall', this.call)
      // if (call?.relationships?.campaign?.data?.id) {
      this.getAndSetDispositions(call?.relationships?.campaign?.data?.id)
      // }
    },
    async getAndSetDispositions(campaignId?: number) {
      const api = useLetsDialAPI()
      const authStore = useAuthStore()
      if (
        ['customCalls', 'flowControllCalls'].includes(
          this.call?.attributes.callType || '',
        ) &&
        authStore.selectedOrganization?.id
      ) {
        const disposition = await api.organizationDispositions.list({
          filter: {
            organizationId: authStore.selectedOrganization?.id,
          },
        })
        this.organizationDispositions = disposition.data ?? []
      }
      if (campaignId) {
        const disposition = await api.campaignDispositions.list({
          filter: {
            campaignId: campaignId,
          },
        })
        this.campaignDispositions = disposition.data ?? []
      }
    },
    setError(error?: string) {
      this.error = error
    },
  },
})
